import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, FolderOpen, GripVertical, AlertCircle, X, Gift, Crown, Ban } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Categories() {
  const [establishment, setEstablishment] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    description: '', 
    order: 0,
    addons: [],
    free_addons_limit: 0
  });

  const fetchEstablishment = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/establishments`);
      if (response.data.length > 0) {
        setEstablishment(response.data[0]);
        setError(null);
      } else {
        setError('no_establishment');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error:', err);
      setError('fetch_error');
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    if (!establishment) return;
    try {
      const response = await axios.get(`${API}/establishments/${establishment.id}/categories`);
      setCategories(response.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [establishment]);

  useEffect(() => {
    fetchEstablishment();
  }, [fetchEstablishment]);

  useEffect(() => {
    if (establishment) {
      fetchCategories();
    }
  }, [establishment, fetchCategories]);

  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({ 
        name: category.name, 
        description: category.description, 
        order: category.order,
        addons: category.addons || [],
        free_addons_limit: category.free_addons_limit || 0
      });
    } else {
      setEditingCategory(null);
      setFormData({ 
        name: '', 
        description: '', 
        order: categories.length,
        addons: [],
        free_addons_limit: 0
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        free_addons_limit: parseInt(formData.free_addons_limit) || 0,
        addons: formData.addons.map(addon => ({
          name: addon.name,
          price: parseFloat(addon.price) || 0,
          is_required: addon.is_required || false,
          max_quantity: parseInt(addon.max_quantity) || 1,
          is_premium: addon.is_premium || false,
          is_available: addon.is_available !== false
        }))
      };

      if (editingCategory) {
        await axios.put(`${API}/establishments/${establishment.id}/categories/${editingCategory.id}`, data);
        toast.success('Categoria atualizada!');
      } else {
        await axios.post(`${API}/establishments/${establishment.id}/categories`, data);
        toast.success('Categoria criada!');
      }
      setShowDialog(false);
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar categoria');
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Deseja excluir esta categoria? Os produtos associados não serão excluídos.')) return;
    try {
      await axios.delete(`${API}/establishments/${establishment.id}/categories/${categoryId}`);
      toast.success('Categoria excluída!');
      fetchCategories();
    } catch (err) {
      toast.error('Erro ao excluir categoria');
    }
  };

  const addAddon = () => {
    setFormData(prev => ({
      ...prev,
      addons: [...prev.addons, { name: '', price: '0', is_required: false, max_quantity: 1, is_premium: false, is_available: true }]
    }));
  };

  const updateAddon = (index, field, value) => {
    setFormData(prev => {
      const addons = [...prev.addons];
      addons[index] = { ...addons[index], [field]: value };
      return { ...prev, addons };
    });
  };

  const removeAddon = (index) => {
    setFormData(prev => ({
      ...prev,
      addons: prev.addons.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <AdminLayout establishmentName={establishment?.name}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error === 'no_establishment') {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center h-[60vh] text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mb-4" />
          <h2 className="text-2xl font-bold text-slate-700 mb-2">Nenhum estabelecimento vinculado</h2>
          <p className="text-slate-500">Entre em contato com o administrador do sistema.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout establishmentName={establishment?.name}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Categorias</h1>
          <p className="text-slate-500">Organize os produtos em categorias com adicionais padrão</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="bg-red-500 hover:bg-red-600" data-testid="add-category-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nova Categoria
        </Button>
      </div>

      {categories.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">Nenhuma categoria</h3>
            <p className="text-slate-500 mb-4">Comece criando sua primeira categoria</p>
            <Button onClick={() => handleOpenDialog()} className="bg-red-500 hover:bg-red-600">
              <Plus className="w-4 h-4 mr-2" />
              Criar Categoria
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {categories.map((category) => (
            <Card key={category.id} data-testid={`category-card-${category.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="text-slate-400">
                  <GripVertical className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-800">{category.name}</h3>
                  {category.description && (
                    <p className="text-sm text-slate-500">{category.description}</p>
                  )}
                  {category.addons?.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                        {category.addons.length} adicional(is)
                      </span>
                      {category.free_addons_limit > 0 && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded flex items-center gap-1">
                          <Gift className="w-3 h-3" />
                          {category.free_addons_limit} grátis
                        </span>
                      )}
                      {category.addons.some(a => a.is_premium) && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded flex items-center gap-1">
                          <Crown className="w-3 h-3" />
                          Premium
                        </span>
                      )}
                      {category.addons.some(a => a.is_available === false) && (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded flex items-center gap-1">
                          <Ban className="w-3 h-3" />
                          {category.addons.filter(a => a.is_available === false).length} indisponível(is)
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleOpenDialog(category)} data-testid={`edit-category-${category.id}`}>
                    <Pencil className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(category.id)} data-testid={`delete-category-${category.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Editar Categoria' : 'Nova Categoria'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Nome da Categoria</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Hambúrgueres"
                  required
                  data-testid="category-name-input"
                />
              </div>
              <div>
                <Label htmlFor="description">Descrição (opcional)</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descreva a categoria..."
                  data-testid="category-description-input"
                />
              </div>
              <div>
                <Label htmlFor="order">Ordem</Label>
                <Input
                  id="order"
                  type="number"
                  min="0"
                  value={formData.order}
                  onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                  data-testid="category-order-input"
                />
              </div>

              {/* Category Addons */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <Label className="text-base">Adicionais da Categoria</Label>
                    <p className="text-xs text-slate-500 mt-1">
                      Esses adicionais aparecerão em todos os produtos desta categoria
                    </p>
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={addAddon} data-testid="add-category-addon-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar
                  </Button>
                </div>
                
                {formData.addons.map((addon, index) => (
                  <div key={index} className="mb-3 p-3 bg-slate-50 rounded-lg">
                    <div className="flex gap-2 items-start mb-2">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Nome do adicional"
                          value={addon.name}
                          onChange={(e) => updateAddon(index, 'name', e.target.value)}
                          data-testid={`category-addon-name-${index}`}
                        />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Preço"
                          value={addon.price}
                          onChange={(e) => updateAddon(index, 'price', e.target.value)}
                          data-testid={`category-addon-price-${index}`}
                        />
                      </div>
                      <Button type="button" size="icon" variant="ghost" onClick={() => removeAddon(index)}>
                        <X className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={addon.is_premium || false}
                          onCheckedChange={(checked) => updateAddon(index, 'is_premium', checked)}
                          data-testid={`category-addon-premium-${index}`}
                        />
                        <Label className="text-sm flex items-center gap-1">
                          <Crown className="w-3 h-3 text-amber-500" />
                          Premium
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={addon.is_available !== false}
                          onCheckedChange={(checked) => updateAddon(index, 'is_available', checked)}
                          data-testid={`category-addon-available-${index}`}
                        />
                        <Label className={`text-sm flex items-center gap-1 ${addon.is_available === false ? 'text-red-600' : ''}`}>
                          {addon.is_available === false ? (
                            <>
                              <Ban className="w-3 h-3 text-red-500" />
                              Indisponível
                            </>
                          ) : (
                            'Disponível'
                          )}
                        </Label>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Free Addons Limit */}
                {formData.addons.length > 0 && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Gift className="w-4 h-4 text-green-600" />
                      <Label className="text-green-800 font-medium">Limite de Adicionais Grátis</Label>
                    </div>
                    <p className="text-sm text-green-700 mb-3">
                      Defina quantos adicionais <strong>não-premium</strong> o cliente pode escolher sem custo extra.
                      Itens marcados como <strong>Premium</strong> são sempre cobrados.
                    </p>
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        min="0"
                        max={formData.addons.filter(a => !a.is_premium).length}
                        value={formData.free_addons_limit}
                        onChange={(e) => setFormData(prev => ({ ...prev, free_addons_limit: e.target.value }))}
                        className="w-24"
                        data-testid="category-free-addons-limit-input"
                      />
                      <span className="text-sm text-green-700">
                        {formData.free_addons_limit > 0 
                          ? `Primeiros ${formData.free_addons_limit} adicional(is) não-premium grátis`
                          : 'Todos os adicionais são cobrados'
                        }
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-red-500 hover:bg-red-600" data-testid="save-category-btn">
                {editingCategory ? 'Salvar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
